import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Pencil, Save, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const [shops, setShops] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchShops();
  }, []);

  const fetchShops = async () => {
    try {
      const response = await axios.get(`${API}/shops`);
      setShops(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching shops:', error);
      setLoading(false);
    }
  };

  const handleEdit = (shop) => {
    setEditingId(shop.id);
    setEditForm({
      name: shop.name,
      address: shop.address,
      description: shop.description,
      hours: shop.hours,
      image_url: shop.image_url,
      rating: shop.rating
    });
  };

  const handleSave = async (shopId) => {
    try {
      await axios.put(`${API}/shops/${shopId}`, editForm);
      setEditingId(null);
      fetchShops();
    } catch (error) {
      console.error('Error updating shop:', error);
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditForm({});
  };

  if (loading) {
    return <div className="p-12">Loading...</div>;
  }

  return (
    <div className="min-h-screen grain-overlay px-6 md:px-12 lg:px-24 py-12">
      <h1 className="text-5xl font-semibold tracking-tight mb-8" data-testid="admin-title">
        Admin Panel - Update Coffee Shop Photos
      </h1>
      <p className="text-lg text-muted-foreground mb-12">
        Update image URLs with photos from Google Maps or Facebook pages
      </p>

      <div className="space-y-6">
        {shops.map((shop) => (
          <Card key={shop.id} className="border-border">
            <CardHeader>
              <CardTitle className="text-2xl">{shop.name}</CardTitle>
            </CardHeader>
            <CardContent>
              {editingId === shop.id ? (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Shop Name</label>
                    <Input
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      data-testid={`edit-name-${shop.id}`}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Address</label>
                    <Input
                      value={editForm.address}
                      onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                      data-testid={`edit-address-${shop.id}`}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Description</label>
                    <textarea
                      value={editForm.description}
                      onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                      className="w-full min-h-[100px] px-3 py-2 border border-input rounded-lg"
                      data-testid={`edit-description-${shop.id}`}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Hours</label>
                    <Input
                      value={editForm.hours}
                      onChange={(e) => setEditForm({ ...editForm, hours: e.target.value })}
                      data-testid={`edit-hours-${shop.id}`}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">
                      Image URL (from Google Maps or Facebook)
                    </label>
                    <Input
                      value={editForm.image_url}
                      onChange={(e) => setEditForm({ ...editForm, image_url: e.target.value })}
                      placeholder="https://..."
                      data-testid={`edit-image-${shop.id}`}
                    />
                    {editForm.image_url && (
                      <img
                        src={editForm.image_url}
                        alt="Preview"
                        className="mt-4 w-full max-w-md h-48 object-cover rounded-lg"
                      />
                    )}
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Rating (0-5)</label>
                    <Input
                      type="number"
                      step="0.1"
                      min="0"
                      max="5"
                      value={editForm.rating}
                      onChange={(e) => setEditForm({ ...editForm, rating: parseFloat(e.target.value) })}
                      data-testid={`edit-rating-${shop.id}`}
                    />
                  </div>
                  <div className="flex gap-3">
                    <Button
                      onClick={() => handleSave(shop.id)}
                      className="bg-accent hover:bg-accent/90"
                      data-testid={`save-button-${shop.id}`}
                    >
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </Button>
                    <Button
                      onClick={handleCancel}
                      variant="outline"
                      data-testid={`cancel-button-${shop.id}`}
                    >
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-6">
                  <img
                    src={shop.image_url}
                    alt={shop.name}
                    className="w-48 h-48 object-cover rounded-lg"
                  />
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground mb-2">{shop.address}</p>
                    <p className="text-sm mb-2">{shop.description}</p>
                    <p className="text-sm text-muted-foreground mb-2">Hours: {shop.hours}</p>
                    <p className="text-sm font-medium mb-4">Rating: {shop.rating}/5</p>
                    <Button
                      onClick={() => handleEdit(shop)}
                      variant="outline"
                      size="sm"
                      data-testid={`edit-button-${shop.id}`}
                    >
                      <Pencil className="h-4 w-4 mr-2" />
                      Edit Photos & Details
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>

      <Card className="mt-12 bg-muted/30 border-border">
        <CardContent className="p-8">
          <h3 className="text-xl font-semibold mb-4">How to Get Business Photos:</h3>
          <div className="space-y-4 text-sm">
            <div>
              <h4 className="font-medium mb-2">From Google Maps:</h4>
              <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                <li>Search for the coffee shop on Google Maps</li>
                <li>Click on the "Photos" section</li>
                <li>Right-click on a photo and select "Open image in new tab"</li>
                <li>Copy the URL from the address bar</li>
                <li>Paste it in the Image URL field above</li>
              </ol>
            </div>
            <div>
              <h4 className="font-medium mb-2">From Facebook:</h4>
              <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                <li>Visit the coffee shop's Facebook page</li>
                <li>Go to their Photos section</li>
                <li>Right-click on a photo and select "Open image in new tab"</li>
                <li>Copy the URL from the address bar</li>
                <li>Paste it in the Image URL field above</li>
              </ol>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Admin;
